import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from './api';

export type AccountRequestStatus = 'pending' | 'approved' | 'denied';

export interface AccountRequest {
  id: number;
  email: string;
  display_name: string | null;
  requested_org_id: number | null;
  requested_org_name: string | null;
  reason: string | null;
  status: AccountRequestStatus;
  created_at: string;
  decided_at: string | null;
  decided_by_user_id: number | null;
  decision_note: string | null;
}

export interface AccountRequestDecision {
  note?: string | null;
  organization_id?: number | null;
}

export interface JoinableOrg {
  id: number;
  name: string;
}

const ADMIN_QUEUE_KEY = ['admin', 'account-requests'] as const;
const ORG_QUEUE_KEY = (slug: string) =>
  ['orgs', slug, 'account-requests'] as const;
const ME_PENDING_COUNT_KEY = ['me', 'account-requests', 'pending-count'] as const;

export function useJoinableOrgs() {
  return useQuery<JoinableOrg[]>({
    queryKey: ['settings', 'joinable-orgs'],
    queryFn: () => api.get<JoinableOrg[]>('/api/settings/joinable-orgs'),
    staleTime: 60_000,
  });
}

// --- Site-admin scope ----------------------------------------------------

export function useAdminAccountRequests(statusFilter: 'pending' | 'all' = 'pending') {
  return useQuery<AccountRequest[]>({
    queryKey: [...ADMIN_QUEUE_KEY, statusFilter],
    queryFn: () =>
      api.get<AccountRequest[]>(
        `/api/admin/account-requests?status_filter=${statusFilter}`,
      ),
  });
}

/**
 * Unified pending count — works for both site admins (sees all) and
 * org admins (sees only their orgs' targeted requests). Replaces the
 * previous admin-only endpoint as the badge data source.
 */
export function useMyPendingAccountRequestsCount() {
  return useQuery<{ count: number }>({
    queryKey: ME_PENDING_COUNT_KEY,
    queryFn: () =>
      api.get<{ count: number }>('/api/me/account-requests/pending-count'),
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
}

function invalidateAll(qc: ReturnType<typeof useQueryClient>, orgSlug?: string) {
  qc.invalidateQueries({ queryKey: ADMIN_QUEUE_KEY });
  qc.invalidateQueries({ queryKey: ME_PENDING_COUNT_KEY });
  qc.invalidateQueries({ queryKey: ['admin', 'users'] });
  if (orgSlug) {
    qc.invalidateQueries({ queryKey: ORG_QUEUE_KEY(orgSlug) });
  }
}

export function useApproveAccountRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: AccountRequestDecision }) =>
      api.postJson<AccountRequest>(
        `/api/admin/account-requests/${id}/approve`,
        body,
      ),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useDenyAccountRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: AccountRequestDecision }) =>
      api.postJson<AccountRequest>(
        `/api/admin/account-requests/${id}/deny`,
        body,
      ),
    onSuccess: () => invalidateAll(qc),
  });
}

// --- Org-admin scope -----------------------------------------------------
//
// Mirror of the site-admin queue, scoped to a single org. Used by the
// per-org account-requests panel on the OrganizationPage.

export function useOrgAccountRequests(
  orgSlug: string | undefined,
  statusFilter: 'pending' | 'all' = 'pending',
) {
  return useQuery<AccountRequest[]>({
    queryKey: orgSlug
      ? [...ORG_QUEUE_KEY(orgSlug), statusFilter]
      : ['orgs', '_none', 'account-requests'],
    queryFn: () =>
      api.get<AccountRequest[]>(
        `/api/orgs/${orgSlug}/account-requests?status_filter=${statusFilter}`,
      ),
    enabled: !!orgSlug,
  });
}

export function useApproveOrgAccountRequest(orgSlug: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: AccountRequestDecision }) =>
      api.postJson<AccountRequest>(
        `/api/orgs/${orgSlug}/account-requests/${id}/approve`,
        body,
      ),
    onSuccess: () => invalidateAll(qc, orgSlug),
  });
}

export function useDenyOrgAccountRequest(orgSlug: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: AccountRequestDecision }) =>
      api.postJson<AccountRequest>(
        `/api/orgs/${orgSlug}/account-requests/${id}/deny`,
        body,
      ),
    onSuccess: () => invalidateAll(qc, orgSlug),
  });
}
